import amapi from '@/api/amapi'

const initialState = () => ({
  databases: [],
  users: [],
  restrictions: {},
  privileges: [],
  remoteHosts: []
})

const state = initialState

const getters = {
  mysqlDatabases: state => state.databases,
  mysqlUsers: state => state.users,
  mysqlRestrictions: state => state.restrictions,
  mysqlPrivileges: state => state.privileges,
  mysqlRemoteHosts: state => state.remoteHosts
}

const mutations = {
  SET_MYSQL_DATABASES: (state, databases) => {
    state.databases = databases
  },
  SET_MYSQL_USERS: (state, users) => {
    state.users = users
  },
  SET_MYSQL_RESTRICTIONS: (state, restrictions) => {
    state.restrictions = restrictions
  },
  SET_MYSQL_PRIVILEGES: (state, privileges) => {
    state.privileges = privileges
  },
  SET_MYSQL_REMOTE_HOSTS: (state, hosts) => {
    state.remoteHosts = hosts
  },
  CLEAR_MYSQL_STORE (state) {
    const s = initialState()

    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  }
}

const actions = {
  async getMysqlRestrictions ({ dispatch, commit }, _username) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'get_restrictions',
        _username,
      }
      const response = await amapi.cpanel_uapi({ ...data })
      commit('SET_MYSQL_RESTRICTIONS', response.data)
    } catch (e) {
      throw e
    }
  },
  // Synchronise state with API data
  async getMysqlDatabases ({ dispatch, commit }, _username) {
    try {
      const data = {
        api2_module: 'MysqlFE',
        api2_method: 'listdbs',
        _username
      }
      const response = await amapi.cpanel_api2({ ...data })
      commit('SET_MYSQL_DATABASES', response.data)
    } catch (e) {
      commit('SET_MYSQL_DATABASES', [])
      throw e
    }
  },
  // Delete database
  async deleteMysqlDatabase ({ dispatch, commit }, { name, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'delete_database',
        name,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Create database
  async createMysqlDatabase ({ dispatch, commit }, { name, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'create_database',
        name,
        _username,
      }
      console.log(data)
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Rename database
  async renameMysqlDatabase ({ dispatch, commit }, { oldname, newname, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'rename_database',
        oldname,
        newname,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Create database user
  async createMysqlUser ({ dispatch, commit }, { name, password, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'create_user',
        name,
        password,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Get list of all mysql users
  async getMysqlUsers ({ commit }, _username) {
    const data = {
      uapi_module: 'Mysql',
      uapi_function: 'list_users',
      _username,
    }
    try {
      const response = await amapi.cpanel_uapi(data)
      commit('SET_MYSQL_USERS', response.data)
    } catch (e) {
      commit('SET_MYSQL_USERS', [])
      throw e
    }
  },
  // Get list of all available mysql privileges
  async getMysqlPrivileges ({ dispatch, commit }, _username) {
    try {
      const data = {
        api2_module: 'MysqlFE',
        api2_method: 'getmysqlprivileges',
        _username,
      }
      const response = await amapi.cpanel_api2({ ...data })
      commit('SET_MYSQL_PRIVILEGES', response.data)
    } catch (e) {
      commit('SET_MYSQL_PRIVILEGES', [])
      throw e
    }
  },
  // Get list of privileges for user by database
  async getMysqlUserPrivileges ({ dispatch }, { database, user, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'get_privileges_on_database',
        database,
        user,
        _username,
      }
      const response = await amapi.cpanel_uapi({ ...data })
      return response.data
    } catch (e) {
      throw e
    }
  },
  // Set privileges for user by database
  async setMysqlUserPrivileges ({ dispatch }, { database, user, privileges, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'set_privileges_on_database',
        database,
        user,
        privileges,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Revoke user database privileges
  async revokeMysqlUserAccess ({ dispatch }, { database, user, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'revoke_access_to_database',
        database,
        user,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Delete user
  async deleteMysqlUser ({ dispatch }, { name, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'delete_user',
        name,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Rename user
  async renameMysqlUser ({ dispatch }, { oldname, newname, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'rename_user',
        oldname,
        newname,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Change user password
  async changeMysqlUserPassword ({ dispatch }, { user, password, _username }) {
    const data = {
      uapi_module: 'Mysql',
      uapi_function: 'set_password',
      user,
      password,
      _username,
    }
    try {
      await amapi.cpanel_uapi(data)
    } catch (e) {
      throw e
    }
  },
  // Add host
  async addMysqlAccessHost ({ dispatch }, { host, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'add_host',
        host,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
  // Get list of remote access hosts
  async getMysqlRemoteHosts ({ dispatch, commit }, _username) {
    try {
      const data = {
        api2_module: 'MysqlFE',
        api2_method: 'listhosts',
        _username,
      }
      const response = await amapi.cpanel_api2({ ...data })
      commit('SET_MYSQL_REMOTE_HOSTS', response.data)
    } catch (e) {
      throw e
    }
  },
  // Delete remote access host
  async deleteMysqlRemoteHost ({ dispatch, commit }, { host, _username }) {
    try {
      const data = {
        uapi_module: 'Mysql',
        uapi_function: 'delete_host',
        host,
        _username,
      }
      await amapi.cpanel_uapi({ ...data })
    } catch (e) {
      throw e
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
